import { Configure, Index, InstantSearch } from "react-instantsearch";
import { useSearchIndex } from "../SearchContextProvider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  CARDS_PER_ROW,
  COURSE,
  DEFAULT_SEARCH_CONFIG,
  PROGRAM,
} from "@/components/Search/constants";
import getFilters from "@/components/Search/utils/getFilters";
import getPublicClient from "@/lib/algolia/getPublicClient";
import NoResulsProductList from "./NoResulsProductList";
import useTranslations from "@/i18n/useTranslations";

function NoProductFoundAlert({
  productType,
}: {
  productType: "courses" | "programs";
}) {
  const t = useTranslations("Search");
  return (
    <Alert className="py-6 px-4 rounded-xl">
      <AlertTitle className="text-base font-bold">
        {productType === "courses"
          ? t("noCoursesFoundTitle")
          : t("noProgramsFoundTitle")}
      </AlertTitle>
      <AlertDescription>
        {productType === "courses"
          ? t("checkOutPopularCourses")
          : t("checkOutPopularPrograms")}
      </AlertDescription>
    </Alert>
  );
}

const searchClient = getPublicClient();

function SuggestedCourses({
  searchIndex,
  configForSearch,
}: {
  searchIndex: string;
  configForSearch: typeof DEFAULT_SEARCH_CONFIG;
}) {
  const t = useTranslations("Search");
  return (
    <div>
      <h3 className="text-lg">{t("popularCourses")}</h3>
      <NoProductFoundAlert productType="courses" />
      <Index indexName={searchIndex} indexId="promotedCourses">
        <Configure
          {...configForSearch}
          facetsRefinements={{ product: [COURSE] }}
        />
        <NoResulsProductList />
      </Index>
    </div>
  );
}

function SuggestedPrograms({
  searchIndex,
  configForSearch,
  titleComponent,
}: {
  searchIndex: string;
  configForSearch: typeof DEFAULT_SEARCH_CONFIG;
  titleComponent?: React.ReactNode;
}) {
  const t = useTranslations("Search");
  return (
    <div>
      <div>
        {titleComponent || <h3 className="text-lg">{t("popularPrograms")}</h3>}
        <NoProductFoundAlert productType="programs" />
        <Index indexName={searchIndex} indexId="promotedPrograms">
          <Configure
            {...configForSearch}
            facetsRefinements={{ product: [PROGRAM] }}
          />
          <NoResulsProductList />
        </Index>
      </div>
    </div>
  );
}

function NoResults({ titleComponent }: { titleComponent?: React.ReactNode }) {
  const { searchIndex } = useSearchIndex();
  const configForSearch = {
    ...DEFAULT_SEARCH_CONFIG,
    hitsPerPage: CARDS_PER_ROW,
    filters: getFilters(),
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={searchIndex}>
      <SuggestedCourses
        searchIndex={searchIndex}
        configForSearch={configForSearch}
      />
      <SuggestedPrograms
        searchIndex={searchIndex}
        configForSearch={configForSearch}
        titleComponent={titleComponent}
      />
    </InstantSearch>
  );
}

export function NoResultsCourses() {
  const { searchIndex } = useSearchIndex();
  const configForSearch = {
    ...DEFAULT_SEARCH_CONFIG,
    hitsPerPage: CARDS_PER_ROW,
    filters: getFilters(),
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={searchIndex}>
      <SuggestedCourses
        searchIndex={searchIndex}
        configForSearch={configForSearch}
      />
    </InstantSearch>
  );
}
export function NoResultsPrograms({
  titleComponent,
}: {
  titleComponent?: React.ReactNode;
}) {
  const { searchIndex } = useSearchIndex();
  const configForSearch = {
    ...DEFAULT_SEARCH_CONFIG,
    hitsPerPage: CARDS_PER_ROW,
    filters: getFilters(),
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={searchIndex}>
      <SuggestedPrograms
        searchIndex={searchIndex}
        configForSearch={configForSearch}
        titleComponent={titleComponent}
      />
    </InstantSearch>
  );
}

export default NoResults;
